/**
 * Real backend adapter for the ops console.
 *
 * The third auth realm. Ops sessions are IP-allowlisted server-side and every
 * mutation is audited there — this client only reads that trail.
 */
import { ApiRequestError } from './client'
import type { OpsApi, VettingQueueFilters } from './ops-client'
import type {
  AuditEntry, CourseDraft, Dispute, DisputeStatus, Interview, InterviewMode,
  OpsOrganization, OpsUser, Page, PayoutRun, PlatformMetrics, RateCard,
  RateFloor, ScreeningResult, Skill, Tier, VettingQueueItem, Worker360, WorkerDocument,
} from './types'

const TOKEN_KEY = 'pinchcrew.ops.token'
const REFRESH_KEY = 'pinchcrew.ops.refresh'

function read(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function write(key: string, value: string | null): void {
  try { value === null ? localStorage.removeItem(key) : localStorage.setItem(key, value) } catch { /* private mode */ }
}

export class OpsHttpApi implements OpsApi {
  private refreshInFlight: Promise<string> | null = null

  constructor(private readonly baseUrl: string) {}

  private async request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
    const headers = new Headers(init.headers)
    const token = read(TOKEN_KEY)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    if (!(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')

    const res = await fetch(`${this.baseUrl}${path}`, { ...init, headers })

    if (res.status === 401 && retry && read(REFRESH_KEY)) {
      await this.refresh()
      return this.request<T>(path, init, false)
    }
    if (!res.ok) {
      let body: { code?: string; message?: string; field_errors?: Record<string, string[]> } = {}
      try { body = await res.json() } catch { /* non-JSON error */ }
      throw new ApiRequestError(
        body.code ?? 'http_error',
        body.message ?? `Request failed (${res.status})`,
        body.field_errors, res.status,
      )
    }
    if (res.status === 204) return undefined as T
    return res.json() as Promise<T>
  }

  private refresh(): Promise<string> {
    if (this.refreshInFlight) return this.refreshInFlight
    this.refreshInFlight = (async () => {
      const res = await fetch(`${this.baseUrl}/ops/auth/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: read(REFRESH_KEY) }),
      })
      if (!res.ok) {
        write(TOKEN_KEY, null); write(REFRESH_KEY, null)
        throw new ApiRequestError('session_expired', 'Please sign in again.', undefined, 401)
      }
      const data = await res.json() as { access: string; refresh?: string }
      write(TOKEN_KEY, data.access)
      if (data.refresh) write(REFRESH_KEY, data.refresh)
      return data.access
    })().finally(() => { this.refreshInFlight = null })
    return this.refreshInFlight
  }

  private post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined })
  }

  // ---- auth ----
  async login(email: string, password: string, totp?: string) {
    const data = await this.post<{ access: string; refresh: string; user: OpsUser }>(
      '/ops/auth/login/', { email, password, totp },
    )
    write(TOKEN_KEY, data.access)
    write(REFRESH_KEY, data.refresh)
    return { user: data.user }
  }
  /** True when a token survives a reload, so the app can restore the session. */
  hasStoredSession(): boolean {
    return read(TOKEN_KEY) !== null
  }

  async logout() { write(TOKEN_KEY, null); write(REFRESH_KEY, null) }
  getMe() { return this.request<OpsUser>('/ops/me/') }

  // ---- vetting ----
  getVettingQueue(filters: VettingQueueFilters = {}) {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(filters)) if (v !== undefined) q.set(k, String(v))
    return this.request<Page<VettingQueueItem>>(`/ops/vetting/queue/?${q}`)
  }
  getWorker360(workerId: string) { return this.request<Worker360>(`/ops/workers/${workerId}/`) }
  /**
   * Fetch a document as an object URL for display.
   *
   * The file endpoint requires a Bearer token, so it cannot go straight into
   * an <img src> — the browser would not send the header. The caller MUST
   * revokeObjectURL when done, or every document opened leaks its blob for
   * the life of the tab.
   */
  getScreening(workerId: string) {
    return this.request<ScreeningResult>(`/ops/workers/${workerId}/screening/`)
  }

  async getDocumentFile(id: string): Promise<{ url: string; contentType: string }> {
    const headers = new Headers()
    const token = read(TOKEN_KEY)
    if (token) headers.set('Authorization', `Bearer ${token}`)

    let res = await fetch(`${this.baseUrl}/ops/documents/${id}/file/`, { headers })
    if (res.status === 401 && read(REFRESH_KEY)) {
      await this.refresh()
      const retryHeaders = new Headers()
      const fresh = read(TOKEN_KEY)
      if (fresh) retryHeaders.set('Authorization', `Bearer ${fresh}`)
      res = await fetch(`${this.baseUrl}/ops/documents/${id}/file/`, { headers: retryHeaders })
    }
    if (!res.ok) {
      let body: { code?: string; message?: string } = {}
      try { body = await res.json() } catch { /* binary or empty */ }
      throw new ApiRequestError(
        body.code ?? 'http_error',
        body.message ?? `Could not load the document (${res.status})`,
        undefined, res.status,
      )
    }
    const blob = await res.blob()
    return { url: URL.createObjectURL(blob), contentType: blob.type }
  }

  verifyDocument(id: string) { return this.post<WorkerDocument>(`/ops/documents/${id}/verify/`) }
  rejectDocument(id: string, reason: string) { return this.post<WorkerDocument>(`/ops/documents/${id}/reject/`, { reason }) }
  passStage(workerId: string, stageSlug: string, note?: string) {
    return this.post<Worker360>(`/ops/workers/${workerId}/stages/${stageSlug}/pass/`, { note })
  }
  failStage(workerId: string, stageSlug: string, reason: string) {
    return this.post<Worker360>(`/ops/workers/${workerId}/stages/${stageSlug}/fail/`, { reason })
  }
  scheduleInterview(workerId: string, at: string, mode: InterviewMode) {
    return this.post<Interview>(`/ops/workers/${workerId}/interviews/`, { scheduled_at: at, mode })
  }
  recordInterview(id: string, outcome: 'passed' | 'failed' | 'no_show', notes?: string) {
    return this.post<Interview>(`/ops/interviews/${id}/record/`, { outcome, notes })
  }
  activateWorker(workerId: string) { return this.post<Worker360>(`/ops/workers/${workerId}/activate/`) }
  suspendWorker(workerId: string, reason: string, days: number) {
    return this.post<Worker360>(`/ops/workers/${workerId}/suspend/`, { reason, days })
  }
  waiveSuspension(id: string, reason: string) {
    return this.post<Worker360>(`/ops/suspensions/${id}/waive/`, { reason })
  }

  // ---- disputes ----
  listDisputes(status?: DisputeStatus) {
    return this.request<Dispute[]>(`/ops/disputes/${status ? `?status=${status}` : ''}`)
  }
  resolveDispute(id: string, outcome: string, paidMinutes: number, note: string) {
    return this.post<Dispute>(`/ops/disputes/${id}/resolve/`, { outcome, paid_minutes: paidMinutes, note })
  }

  // ---- payout runs ----
  listPayoutRuns() { return this.request<PayoutRun[]>('/ops/payout-runs/') }
  getPayoutRun(id: string) { return this.request<PayoutRun>(`/ops/payout-runs/${id}/`) }
  preparePayoutRun(periodStart: string, periodEnd: string) {
    return this.post<PayoutRun>('/ops/payout-runs/', { period_start: periodStart, period_end: periodEnd })
  }
  releasePayoutRun(id: string) { return this.post<PayoutRun>(`/ops/payout-runs/${id}/release/`) }

  // ---- orgs ----
  listOrganizations() { return this.request<OpsOrganization[]>('/ops/organizations/') }
  createOrganization(input: Record<string, unknown>) {
    return this.post<OpsOrganization>('/ops/organizations/', input)
  }
  updateOrganization(id: string, patch: Partial<OpsOrganization>) {
    return this.request<OpsOrganization>(`/ops/organizations/${id}/`, { method: 'PATCH', body: JSON.stringify(patch) })
  }
  suspendOrganization(id: string, reason: string) {
    return this.post<OpsOrganization>(`/ops/organizations/${id}/suspend/`, { reason })
  }

  // ---- rates ----
  listRateCards() { return this.request<RateCard[]>('/ops/rate-cards/') }
  listRateFloors() { return this.request<RateFloor[]>('/ops/rate-floors/') }
  upsertRateCard(input: Record<string, unknown>) { return this.post<RateCard>('/ops/rate-cards/', input) }
  setRateFloor(skillSlug: string, tier: Tier, minPayRate: string) {
    return this.post<RateFloor>('/ops/rate-floors/', { skill_slug: skillSlug, tier, min_pay_rate: minPayRate })
  }

  // ---- analytics ----
  getMetrics(period: '7d' | '30d' | '90d' = '30d') {
    return this.request<PlatformMetrics>(`/ops/metrics/?period=${period}`)
  }

  // ---- course authoring ----
  listCourseDrafts() { return this.request<CourseDraft[]>('/ops/courses/') }
  getCourseDraft(slug: string) { return this.request<CourseDraft>(`/ops/courses/${slug}/`) }
  saveCourseDraft(draft: CourseDraft) {
    return this.request<CourseDraft>(`/ops/courses/${draft.slug}/`, { method: 'PUT', body: JSON.stringify(draft) })
  }
  publishCourse(slug: string) { return this.post<CourseDraft>(`/ops/courses/${slug}/publish/`) }

  // ---- audit ----
  listAudit(limit = 50) { return this.request<AuditEntry[]>(`/ops/audit/?limit=${limit}`) }

  // ---- reference ----
  listSkills() { return this.request<Skill[]>('/ops/skills/') }
}
